import { z } from 'zod';
/**
 * "Meu negócio" (plano PRO): o que o Secretário precisa saber para atender os
 * clientes do dono. O tipo de negócio escolhe o roteiro de atendimento da área.
 */
export const BUSINESS_KINDS = [
    'medico',
    'dentista',
    'salao',
    'advocacia',
    'nutricao',
    'contabilidade',
    'servicos_casa',
    'loja',
    'outro',
];
export const businessKindSchema = z.enum(BUSINESS_KINDS);
/** Como cada tipo aparece na escolha do negócio. */
export const BUSINESS_KIND_INFO = {
    medico: { label: 'Médico', hint: 'Consultório ou clínica' },
    dentista: { label: 'Dentista', hint: 'Consultório odontológico' },
    salao: { label: 'Salão de beleza', hint: 'Cabelo, barba, unhas e estética' },
    advocacia: { label: 'Advocacia', hint: 'Escritório de advogado' },
    nutricao: { label: 'Nutricionista', hint: 'Consultório de nutrição' },
    contabilidade: { label: 'Contabilidade', hint: 'Escritório contábil' },
    servicos_casa: { label: 'Serviços em casa', hint: 'Eletricista, encanador, reparos' },
    loja: { label: 'Loja', hint: 'Comércio e pedidos' },
    outro: { label: 'Outro', hint: 'Me conta qual é' },
};
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const businessServiceSchema = z.object({
    name: z.string().trim().min(1).max(60),
    /** Preço em centavos; null quando é "a combinar". */
    price: z.number().int().positive().max(100_000_000).nullable(),
    /** Duração em minutos, quando faz sentido. */
    minutes: z.number().int().positive().max(1440).nullable(),
});
export const businessHoursSchema = z.object({
    /** 0 = domingo … 6 = sábado. */
    day: z.number().int().min(0).max(6),
    open: time,
    close: time,
});
export const businessProfileSchema = z.object({
    kind: businessKindSchema,
    /** Para "Outro": o que é o negócio, nas palavras do dono. */
    kindLabel: z.string().trim().max(60).nullable(),
    name: z.string().trim().min(1).max(80),
    about: z.string().trim().max(400).nullable(),
    address: z.string().trim().max(160).nullable(),
    services: z.array(businessServiceSchema).max(24),
    hours: z.array(businessHoursSchema).max(14),
    /** O Secretário pode dizer os preços da tabela sem perguntar ao dono? */
    sharePrices: z.boolean(),
    /** Palavras que fazem o Secretário chamar o dono na hora. */
    callOwnerWords: z.array(z.string().trim().min(2).max(30)).max(12),
    /** Ajustes do dono ao roteiro da área ("Como eu atendo seus clientes"). */
    guidance: z.string().trim().max(2500).nullable(),
    /** Atendimento ligado: o Secretário responde clientes e novos contatos do link. */
    active: z.boolean(),
    /** Clientes podem pedir horário pelo Secretário (o dono confirma). */
    bookingEnabled: z.boolean().default(false),
    /** Duração de cada horário oferecido, em minutos. */
    slotMinutes: z.number().int().min(15).max(240).default(60),
    /** Versão do Termo de tratamento de dados do atendimento que o dono aceitou. */
    dpaVersion: z.number().int().nullable().default(null),
    updatedAt: z.iso.datetime(),
});
/** Versão vigente do Termo de tratamento de dados do atendimento (`/termo-negocio`). */
export const BUSINESS_DPA_VERSION = 1;
export const saveBusinessProfileSchema = businessProfileSchema
    .omit({ updatedAt: true, dpaVersion: true })
    .partial()
    .extend({ acceptDpa: z.boolean().optional() })
    .refine((value) => Object.keys(value).length > 0, { message: 'Nada para salvar.' });
/** O que qualquer pessoa vê ao abrir o link de atendimento. */
export const businessCardSchema = z.object({
    username: z.string(),
    name: z.string(),
    kind: businessKindSchema,
    kindLabel: z.string().nullable(),
    about: z.string().nullable(),
    address: z.string().nullable(),
    services: z.array(businessServiceSchema),
    hours: z.array(businessHoursSchema),
    secretary: z.object({ name: z.string(), characterKey: z.string() }),
});
export const quickReplySchema = z.object({
    id: z.uuid(),
    title: z.string().trim().min(1).max(40),
    body: z.string().trim().min(1).max(800),
});
export const saveQuickReplySchema = quickReplySchema.omit({ id: true });
export const businessStateSchema = z.object({
    /** null enquanto o dono não montou o negócio. */
    profile: businessProfileSchema.nullable(),
    /** O plano em vigor inclui o "Meu negócio"? */
    allowed: z.boolean(),
    /** Roteiro de atendimento da área (o ponto de partida que o dono pode ajustar). */
    playbook: z.string().nullable(),
    replies: z.array(quickReplySchema),
});
const DAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
/** "segunda a sexta, 08:00 às 18:00; sábado, 08:00 às 12:00" */
export function describeBusinessHours(hours) {
    if (hours.length === 0)
        return '';
    const sorted = [...hours].sort((a, b) => a.day - b.day || a.open.localeCompare(b.open));
    const groups = [];
    for (const slot of sorted) {
        const last = groups[groups.length - 1];
        if (last && last.to === slot.day - 1 && last.open === slot.open && last.close === slot.close) {
            last.to = slot.day;
        }
        else {
            groups.push({ from: slot.day, to: slot.day, open: slot.open, close: slot.close });
        }
    }
    return groups
        .map((group) => {
        const days = group.from === group.to
            ? DAY_NAMES[group.from]
            : // Dois dias seguidos: "quarta e quinta"; três ou mais: "segunda a sexta".
                `${DAY_NAMES[group.from]} ${group.to === group.from + 1 ? 'e' : 'a'} ${DAY_NAMES[group.to]}`;
        return `${days}, ${group.open} às ${group.close}`;
    })
        .join('; ');
}
/** O negócio está aberto neste instante? (horário de Brasília) */
export function isBusinessOpen(hours, now) {
    if (hours.length === 0)
        return true;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Sao_Paulo',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(now);
    const weekday = parts.find((part) => part.type === 'weekday')?.value ?? '';
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
    const clock = `${parts.find((part) => part.type === 'hour')?.value ?? '00'}:${parts.find((part) => part.type === 'minute')?.value ?? '00'}`;
    return hours.some((slot) => slot.day === day && slot.open <= clock && clock < slot.close);
}
// ── Controle do negócio: ficha do cliente, orçamento, retomadas e painel ──
export const CLIENT_STAGES = [
    'novo',
    'conversando',
    'orcamento',
    'agendado',
    'cliente',
    'perdido',
];
export const clientStageSchema = z.enum(CLIENT_STAGES);
export const CLIENT_STAGE_LABEL = {
    novo: 'Chegou agora',
    conversando: 'Conversando',
    orcamento: 'Orçamento enviado',
    agendado: 'Agendado',
    cliente: 'Cliente',
    perdido: 'Não seguiu',
};
export const businessClientSchema = z.object({
    contactId: z.uuid(),
    name: z.string(),
    username: z.string().nullable(),
    /** Cliente ou Lead, como está nos contatos. */
    category: z.string(),
    stage: clientStageSchema,
    /** Última vez que a pessoa escreveu e o Secretário atendeu. */
    lastInboundAt: z.iso.datetime().nullable(),
    quoteSentAt: z.iso.datetime().nullable(),
    followUps: z.number().int().min(0),
});
export const quoteItemSchema = z.object({
    name: z.string().trim().min(1).max(80),
    quantity: z.number().int().min(1).max(999),
    unitCents: z.number().int().min(0).max(100_000_000),
});
export const quoteStatusSchema = z.enum(['enviado', 'aceito', 'recusado']);
export const businessQuoteSchema = z.object({
    id: z.uuid(),
    contactId: z.uuid().nullable(),
    clientName: z.string(),
    items: z.array(quoteItemSchema),
    discountCents: z.number().int().min(0),
    totalCents: z.number().int().min(0),
    validUntil: z.string(),
    note: z.string().nullable(),
    status: quoteStatusSchema,
    /** Mensagem pronta para mandar na conversa: o que inclui antes do valor. */
    message: z.string(),
    createdAt: z.iso.datetime(),
});
export const createQuoteSchema = z.object({
    contactId: z.uuid().nullable().default(null),
    clientName: z.string().trim().min(1).max(80),
    items: z.array(quoteItemSchema).min(1).max(20),
    discountCents: z.number().int().min(0).max(100_000_000).default(0),
    validDays: z.number().int().min(1).max(90).default(7),
    note: z.string().trim().max(400).nullable().default(null),
});
export const businessClientDetailSchema = businessClientSchema.extend({
    notes: z.string(),
    quotes: z.array(businessQuoteSchema),
});
export const updateClientSchema = z.object({
    stage: clientStageSchema.optional(),
    notes: z.string().max(2000).optional(),
});
/** Cliente que ficou sem resposta depois do orçamento ou da conversa: hora de retomar. */
export const followUpSchema = z.object({
    contactId: z.uuid(),
    name: z.string(),
    username: z.string().nullable(),
    stage: clientStageSchema,
    /** Dias desde o último contato do negócio. */
    days: z.number().int().min(0),
    attempt: z.number().int().min(1),
});
export const businessPanelSchema = z.object({
    /** Últimos 7 dias. */
    replies: z.number().int().min(0),
    calls: z.number().int().min(0),
    visits: z.number().int().min(0),
    newLeads: z.number().int().min(0),
    quotes: z.number().int().min(0),
    quotedCents: z.number().int().min(0),
    accepted: z.number().int().min(0),
    acceptedCents: z.number().int().min(0),
    stages: z.record(z.string(), z.number().int().min(0)),
});
export const businessClientsSchema = z.object({
    clients: z.array(businessClientSchema),
    followUps: z.array(followUpSchema),
    panel: businessPanelSchema,
});
// ── Avisos programados e agenda de atendimento ─────────────────────────────
export const NOTICE_LIMITS = {
    /** Avisos que cada negócio pode programar por dia. */
    perDay: 3,
    /** Pessoas por aviso. */
    perNotice: 200,
    /** Janela de envio, horário de Brasília. */
    fromHour: 8,
    untilHour: 20,
};
export const noticeKindSchema = z.enum(['aviso', 'lembrete']);
export const businessNoticeSchema = z.object({
    id: z.uuid(),
    kind: noticeKindSchema,
    text: z.string(),
    sendAt: z.iso.datetime(),
    status: z.enum(['agendado', 'enviando', 'enviado', 'cancelado']),
    recipients: z.number().int().min(0),
    delivered: z.number().int().min(0),
    createdAt: z.iso.datetime(),
});
export const createNoticeSchema = z.object({
    text: z.string().trim().min(3).max(600),
    /** null = todos os clientes e leads que podem receber. */
    contactIds: z.array(z.uuid()).max(200).nullable().default(null),
    sendAt: z.iso.datetime(),
});
export const noticeAudienceSchema = z.object({
    eligible: z.array(z.object({ contactId: z.uuid(), name: z.string() })),
    /** Pediram para não receber avisos deste negócio. */
    optedOut: z.number().int().min(0),
    /** Já receberam um aviso hoje (um por pessoa por dia). */
    reachedToday: z.number().int().min(0),
    scheduledToday: z.number().int().min(0),
    notices: z.array(businessNoticeSchema),
});
/** Aviso esperando para entrar na conversa de quem recebe. */
export const inboxNoticeSchema = z.object({
    id: z.uuid(),
    kind: noticeKindSchema,
    ownerUsername: z.string(),
    businessName: z.string(),
    text: z.string(),
});
// ── Lembrete a qualquer cliente (G1, negócio comum) ─────────────────────────
export const BUSINESS_VISIT_ANSWERS = ['confirmar', 'remarcar', 'nao_vou'];
export const businessVisitAnswerSchema = z.object({ action: z.enum(BUSINESS_VISIT_ANSWERS) });
export const BUSINESS_ATTENDANCE = ['veio', 'faltou'];
export const businessAttendanceSchema = z.object({
    attendance: z.enum(BUSINESS_ATTENDANCE).nullable(),
});
/** Um horário da agenda do dono com um cliente. */
export const businessVisitSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    contactName: z.string().nullable(),
    /** Telefone do contato, quando tem: é para ele que o lembrete sai. */
    phone: z.string().nullable(),
    answer: z.enum(BUSINESS_VISIT_ANSWERS).nullable(),
    remindedAt: z.iso.datetime().nullable(),
    attendance: z.enum(BUSINESS_ATTENDANCE).nullable(),
    /** O link de confirmar que vai no lembrete. */
    confirmUrl: z.string().nullable(),
});
const absenceSchema = z.object({ total: z.number().int(), missed: z.number().int() });
export const businessVisitsSchema = z.object({
    businessName: z.string(),
    /** Próximas 48 horas: para lembrar. */
    upcoming: z.array(businessVisitSchema),
    /** Últimos 14 dias sem presença marcada: para marcar Veio ou Faltou. */
    recent: z.array(businessVisitSchema),
    /** Faltas nos últimos 90 dias, com e sem lembrete. */
    absence: z.object({ reminded: absenceSchema, notReminded: absenceSchema }),
});
/** O que a página de confirmar mostra a quem recebeu o lembrete (sem conta). */
export const businessVisitViewSchema = z.object({
    businessName: z.string(),
    username: z.string(),
    clientFirstName: z.string(),
    title: z.string(),
    startAt: z.iso.datetime(),
    timezone: z.string(),
    address: z.string().nullable(),
    answer: z.enum(BUSINESS_VISIT_ANSWERS).nullable(),
    /** Já passou, foi cancelado ou apagado: só mostra, não responde. */
    closed: z.boolean(),
    /** Mostra "Quer o lembrete no celular?" (o negócio pode desligar). */
    invite: z.boolean().default(false),
});
// ── Entrada ligada ao negócio (estudo da cortesia, C1): guardar o horário no Secretário ──
export const linkedEntrySchema = z.object({
    kind: z.enum(['visita', 'consulta']),
    /** O nome do negócio (visita) ou o endereço da clínica (consulta). */
    ref: z.string().trim().min(1).max(60),
    token: z.string().min(1).max(100),
});
export const linkedEntryResultSchema = z.object({ startsAt: z.iso.datetime() });
export const clientInviteSchema = z.object({ on: z.boolean() });
/** O lembrete na voz do dono, com o link de confirmar (sem o nome do negócio: evita "na/no"). */
export function businessReminderText(input) {
    const first = (input.clientName ?? '').trim().split(/\s+/)[0] ?? '';
    const hello = first ? `Olá, ${first}!` : 'Olá!';
    const ask = input.confirmUrl
        ? ` Para confirmar ou remarcar, é só tocar: ${input.confirmUrl}`
        : ' Posso confirmar?';
    return `${hello} Passando para lembrar do seu horário ${input.when} com a gente.${ask}`;
}
